import { defineStore } from "pinia";
import type { Message } from "@/types/aiEditor";
import { MESSAGE_TYPES, MESSAGE_STATUS } from "@/constants/aiEditor";

interface AdaptedSize {
  label: string;
  url: string;
  width: number;
  height: number;
}

interface AIEditorState {
  messages: Message[];
  currentPrompt: string;
  currentInputFile: File | null;
  currentInputFiles: File[];
  isProcessing: boolean;

  // Legacy fields (for backwards compatibility during migration)
  inputFile: File | null;
  inputS3Url: string;
  prompt: string;
  outputS3Url: string;

  // Adapted sizes (populated when navigating from Adapt → Edit with AI)
  adaptedSizes: AdaptedSize[];
  selectedSizeForEdit: string | null;
  isPickingFromDesign: boolean;

  isPromptOptimizationNeeded: boolean;
}

export const useAIEditorStore = defineStore("AIEditor", {
  state: (): AIEditorState => ({
    messages: [],
    currentPrompt: "",
    currentInputFile: null,
    currentInputFiles: [],
    isProcessing: false,
    
    inputFile: null,
    inputS3Url: "",
    prompt: "",
    outputS3Url: "",

    // Adapt → Edit with AI
    adaptedSizes: [],
    selectedSizeForEdit: null,
    isPickingFromDesign: false,

    isPromptOptimizationNeeded: true,
  }),

  getters: {
    // Get the last AI message's output URL (for continuous editing)
    lastAIOutputUrl(state): string {
      // Optimized: Iterate backward instead of cloning/reversing the array
      for (let i = state.messages.length - 1; i >= 0; i--) {
        const msg = state.messages[i];
        if (msg.type === "ai" && msg.status === "success" && msg.outputS3Url) {
          return msg.outputS3Url;
        }
      }
      return "";
    },

    // Get AI response for a specific user message
    getAIResponseForUser: (state) => (userMessageId: string) => {
      return state.messages.find(
        (msg) => msg.type === MESSAGE_TYPES.AI && msg.parentMessageId === userMessageId
      );
    },

    // Get user message from AI message
    getUserMessageFromAI: (state) => (aiMessageId: string) => {
      const aiMsg = state.messages.find((msg) => msg.id === aiMessageId);
      if (aiMsg?.parentMessageId) {
        return state.messages.find((msg) => msg.id === aiMsg.parentMessageId);
      }
      return undefined;
    },

    // Check if any message is currently processing
    hasProcessingMessage(state): boolean {
      return state.messages.some((msg) => msg.status === "loading");
    },

    getMessageById: (state) => (messageId: string) => {
      return state.messages.find((msg) => msg.id === messageId);
    },
  },

  actions: {
    // Generate unique ID for messages
    generateMessageId(): string {
      return `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    },

    // Add a user message
    addUserMessage(
      prompt: string,
      inputS3Url?: string,
      parentMessageId?: string,
      isFirstMessage: boolean = false,
      assetS3Urls?: string[]
    ): string {
      const messageId = this.generateMessageId();
      const userMessage: Message = {
        id: messageId,
        type: MESSAGE_TYPES.USER,
        prompt,
        inputS3Url: inputS3Url,
        assetS3Urls: !isFirstMessage ? assetS3Urls : undefined,
        isFirstMessage,
        timestamp: Date.now(),
        parentMessageId,
      };
      this.messages.push(userMessage);
      return messageId;
    },

    // Add an AI message in loading state
    addAIMessage(parentMessageId: string): string {
      const messageId = this.generateMessageId();
      const aiMessage: Message = {
        id: messageId,
        type: MESSAGE_TYPES.AI,
        status: MESSAGE_STATUS.LOADING,
        timestamp: Date.now(),
        parentMessageId,
      };
      this.messages.push(aiMessage);
      this.isProcessing = true;
      return messageId;
    },

    updateUserMessageInputUrl(messageId: string, inputS3Url: string) {
      const message = this.messages.find((msg) => msg.id === messageId);
      if (message?.type === MESSAGE_TYPES.USER) {
        message.inputS3Url = inputS3Url;
      }
    },
    updateAIMessageStatusMessage(messageId: string, statusMessage: string) {
      const message = this.messages.find((msg) => msg.id === messageId);
      if (message && message.type === MESSAGE_TYPES.AI) {
        message.statusMessage = statusMessage;
      }
    },

    // Update AI message with success
    updateAIMessageSuccess(messageId: string, outputS3Url: string) {
      const message = this.messages.find((msg) => msg.id === messageId);
      if (message && message.type === MESSAGE_TYPES.AI) {
        message.outputS3Url = outputS3Url;
        message.status = MESSAGE_STATUS.SUCCESS;
        message.timestamp = Date.now(); // Update timestamp to when response actually arrived
        this.isProcessing = false;
      }
    },

    // Update AI message with error
    updateAIMessageError(messageId: string, error: string) {
      const message = this.messages.find((msg) => msg.id === messageId);
      if (message && message.type === MESSAGE_TYPES.AI) {
        message.status = MESSAGE_STATUS.ERROR;
        message.error = error;
        this.isProcessing = false;
      }
    },

    // Add a system message
    addSystemMessage(prompt: string): string {
      const messageId = this.generateMessageId();
      const systemMessage: Message = {
        id: messageId,
        type: MESSAGE_TYPES.SYSTEM,
        prompt,
        timestamp: Date.now(),
      };
      this.messages.push(systemMessage);
      return messageId;
    },

    // Get the base image URL for continuous editing (last successful AI output)
    getCurrentBaseImageUrl(): string {
      return this.lastAIOutputUrl;
    },

    // Clear all messages
    clearMessages() {
      this.messages = [];
      this.isProcessing = false;
    },

    // Delete messages from a specific message ID onwards (including the message itself)
    deleteMessagesFrom(messageId: string) {
      const index = this.messages.findIndex((msg) => msg.id === messageId);
      if (index !== -1) {
        // Find the next user message after this one
        const nextUserMsgIndex = this.messages.findIndex(
          (msg, idx) => idx > index && msg.type === MESSAGE_TYPES.USER
        );

        // Delete everything from the next message onwards (not including the current message)
        if (nextUserMsgIndex !== -1) {
          this.messages.splice(nextUserMsgIndex);
        }

        // Also delete the AI response to the current message if it exists
        const aiResponseIndex = this.messages.findIndex(
          (msg, idx) =>
            idx > index &&
            msg.type === MESSAGE_TYPES.AI &&
            msg.parentMessageId === messageId
        );
        if (aiResponseIndex !== -1) {
          this.messages.splice(aiResponseIndex, 1);
        }
      }
    },

    // Update user message prompt
    updateUserMessagePrompt(messageId: string, newPrompt: string) {
      const message = this.messages.find((msg) => msg.id === messageId);
      if (message && message.type === MESSAGE_TYPES.USER) {
        message.prompt = newPrompt;
      }
    },

    // Get the image URL before a specific user message (the AI output from the previous iteration)
    getImageBeforeUserMessage(messageId: string): string {
      const index = this.messages.findIndex((msg) => msg.id === messageId);
      if (index === -1) return "";

      // Look backwards for the last successful AI message before this user message
      for (let i = index - 1; i >= 0; i--) {
        const msg = this.messages[i];
        if (msg.type === MESSAGE_TYPES.AI && msg.status === MESSAGE_STATUS.SUCCESS && msg.outputS3Url) {
          return msg.outputS3Url;
        }
      }

      // If not found, return empty string
      return "";
    },

    // Set the creative template ID for a specific message
    setCreativeTemplateIdForMessage(messageId: string, templateId: number) {
      const message = this.messages.find((msg) => msg.id === messageId);
      if (message && message.type === MESSAGE_TYPES.AI) {
        message.creativeTemplateId = templateId;
      }
    },

    // Legacy actions (for backwards compatibility)
    setInputFile(inputFile: File | null) {
      this.inputFile = inputFile;
      this.currentInputFile = inputFile;
      this.currentInputFiles = inputFile ? [inputFile] : [];
      this.inputS3Url = "";
    },

    setInputFiles(files: File[]) {
      this.currentInputFiles = files;
      // Maintain backward compatibility for single file use cases if needed
      this.currentInputFile = files.length > 0 ? files[0] : null;
      this.inputFile = files.length > 0 ? files[0] : null;
    },

    setPrompt(prompt: string) {
      this.prompt = prompt;
      this.currentPrompt = prompt;
    },

    setOutputS3Url(url: string) {
      this.outputS3Url = url;
    },

    setInputS3Url(url: string) {
      this.inputS3Url = url;
      this.inputFile = null;
      this.currentInputFile = null;
    },

    setMessages(messages: Message[]) {
      this.messages = messages;
    },

    setIsProcessing(value: boolean) {
      this.isProcessing = value;
    },

    reset() {
      this.inputFile = null;
      this.inputS3Url = "";
      this.prompt = "";
      this.outputS3Url = "";
      this.currentPrompt = "";
      this.currentInputFile = null;
      this.currentInputFiles = [];
      this.adaptedSizes = [];
      this.selectedSizeForEdit = null;
      this.isPickingFromDesign = false;
      this.isPromptOptimizationNeeded = true;
      this.clearMessages();
    },

    setAdaptedSizes(sizes: AdaptedSize[]) {
      this.adaptedSizes = sizes;
      this.selectedSizeForEdit = null;
      this.isPickingFromDesign = false;
      // Clear any previous session messages so the adapted grid is shown fresh
      this.messages = [];
      this.isProcessing = false;
    },

    setSelectedSizeForEdit(url: string | null) {
      this.selectedSizeForEdit = url;
      this.isPickingFromDesign = false;
    },

    setIsPickingFromDesign(val: boolean) {
      this.isPickingFromDesign = val;
    },

    setIsPromptOptimizationNeeded(val: boolean) {
      this.isPromptOptimizationNeeded = val;
    },
  },
});
